import { Check, Languages, LogOut, Monitor, Moon, Sun, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router';
import { toast } from 'sonner';
import { useDeleteAccount, useLogout, useMe } from '@/api/queries';
import type { User } from '@/api/types';
import { LANGUAGES, currentLanguage } from '@/i18n';
import { cn } from '@/lib/cn';
import { useTheme, type ThemeChoice } from '@/lib/preferences';
import { Button } from './ui/button';
import { Dialog, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from './ui/overlay';

/** Marca: un tablero con una peana encima. */
export function Logo({ className, withText = true }: { className?: string; withText?: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <svg viewBox="0 0 32 32" className="h-8 w-8 shrink-0" aria-hidden>
        <rect width="32" height="32" rx="7" fill="var(--c-ink)" />
        <path d="M8 4v24M16 4v24M24 4v24M4 8h24M4 16h24M4 24h24" stroke="var(--c-line)" strokeWidth="1" opacity=".55" />
        <circle cx="16" cy="16" r="6.5" fill="var(--c-accent)" stroke="var(--c-surface)" strokeWidth="1.6" />
      </svg>
      {withText && <span className="font-display text-[1.35rem] leading-none tracking-wide">{t('app.name')}</span>}
    </span>
  );
}

export function Avatar({ user, size = 32 }: { user: User; size?: number }) {
  const initial = (user.name ?? user.email).charAt(0).toUpperCase();
  return user.avatarUrl ? (
    <img
      src={user.avatarUrl}
      alt=""
      width={size}
      height={size}
      referrerPolicy="no-referrer"
      className="rounded-full border border-line"
    />
  ) : (
    <span
      className="inline-flex items-center justify-center rounded-full bg-accent font-bold text-accent-ink"
      style={{ width: size, height: size, fontSize: size * 0.45 }}
    >
      {initial}
    </span>
  );
}

const THEME_ICONS: Record<ThemeChoice, ReactNode> = { light: <Sun />, dark: <Moon />, system: <Monitor /> };

/** Menú del usuario: tema, idioma y cerrar sesión. */
export function UserMenu() {
  const { t, i18n } = useTranslation();
  const { data: user } = useMe();
  const logout = useLogout();
  const deleteAccount = useDeleteAccount();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { theme, setTheme } = useTheme();
  if (!user) return null;
  const lang = currentLanguage();
  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          <button
            type="button"
            className="rounded-full p-0.5 transition-shadow hover:shadow-[0_0_0_2px_var(--c-line-strong)]"
            aria-label={user.email}
          >
            <Avatar user={user} />
          </button>
        </MenuTrigger>
        <MenuContent>
          <div className="px-2.5 pt-1.5 pb-2">
            <div className="truncate font-medium">{user.name ?? user.email}</div>
            {user.name && <div className="truncate text-sm text-ink-2">{user.email}</div>}
          </div>
          <MenuSeparator />
          <MenuLabel>{t('theme.label')}</MenuLabel>
          {(['light', 'dark', 'system'] as const).map((choice) => (
            <MenuItem key={choice} onSelect={() => setTheme(choice)} icon={THEME_ICONS[choice]}>
              <span className="flex-1">{t(`theme.${choice}`)}</span>
              {theme === choice && <Check size={15} className="text-accent" />}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuLabel>{t('language.label')}</MenuLabel>
          {LANGUAGES.map((code) => (
            <MenuItem key={code} onSelect={() => void i18n.changeLanguage(code)} icon={<Languages />}>
              <span className="flex-1">{t(`language.${code}`)}</span>
              {lang === code && <Check size={15} className="text-accent" />}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem onSelect={() => logout.mutate()} icon={<LogOut />}>
            {t('auth.logout')}
          </MenuItem>
          <MenuSeparator />
          <MenuItem onSelect={() => setConfirmDelete(true)} icon={<Trash2 />} danger>
            {t('account.delete')}
          </MenuItem>
          <div className="px-2.5 pt-1 pb-1.5 text-xs">
            <Link to="/privacidad" className="text-ink-3 hover:text-ink">
              {t('privacy.link')}
            </Link>
          </div>
        </MenuContent>
      </Menu>
      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('account.deleteTitle')}
        description={t('account.deleteBody')}
        footer={
          <>
            <Button onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              disabled={deleteAccount.isPending}
              onClick={() =>
                deleteAccount.mutate(undefined, {
                  onSuccess: () => {
                    setConfirmDelete(false);
                    toast(t('account.deleted'));
                  },
                })
              }
            >
              <Trash2 size={16} />
              {t('account.deleteConfirm')}
            </Button>
          </>
        }
      />
    </>
  );
}

export function FullScreenMessage({ children }: { children: ReactNode }) {
  return <div className="flex h-full items-center justify-center p-6 text-center text-ink-2">{children}</div>;
}

/** Rutas privadas: sin sesión, a /login recordando a dónde se quería ir. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { data: user, isPending, isError, refetch } = useMe();
  const location = useLocation();
  if (isPending) return <FullScreenMessage>{t('common.loading')}</FullScreenMessage>;
  if (isError)
    return (
      <FullScreenMessage>
        <div className="flex flex-col items-center gap-3">
          <p>{t('common.error')}</p>
          <button type="button" className="text-accent underline" onClick={() => void refetch()}>
            {t('common.retry')}
          </button>
        </div>
      </FullScreenMessage>
    );
  if (!user) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return children;
}
